import { CellItem } from '@components/UI-Components/HierarchyWiseItemsTable/components/Row/Cell'
import useOrderContext from '@modules/order/hooks/use-order-context'
import useViewContext from '@modules/order/hooks/use-view-context'
import { PRODUCT_OPERATIONS_LOADERS } from '@modules/order/stores/view/List/ListDataStore'
import { ListUIStore } from '@modules/order/stores/view/List/ListUIStore'
import findIndex from 'lodash/findIndex'
import isNumber from 'lodash/isNumber'
import { useObserver } from 'mobx-react-lite'
import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import QuantityCell, { QuantityCellRef } from '../../components/QuantityCell'
import {
  useExclusionDoor,
  useProductStatus
} from '@components/UI-Components/ProductCard/ProductStatusHook'
import { getNumberFromLocale } from '@utils/get-number-form-locale'
import find from 'lodash/find'
import { config } from '@config'
import { ASSORTMENT_SOURCE } from '@constants'
import { VIPClientType } from '@modules/common/models/enums/VIPClientType'
import { getEnumValue } from '@utils/get-enum-value'
import intersectionBy from 'lodash/intersectionBy'
import isEmpty from 'lodash/isEmpty'
import some from 'lodash/some'
import { stores } from '@stores'

export const Quantity: FC<CellItem> = ({ rowData, column, visibleColIndex, isActive }) =>
  useObserver(function useHook() {
    const viewContextVal = useViewContext()
    const store = viewContextVal.uiStore as ListUIStore
    const {
      showProductSizes,
      bookUnbookDoors,
      getSizes,
      navigateToNextEditableCell,
      navigateToPrevAvailableCell,
      isUnsizedProduct,
      activeItemStore: { setRowAndColIndex, setSizeIndex, sizeIndex },
      dataStore: { itemIndexObjectStore }
    } = store

    const {
      nav: {
        queryParams: { clientType }
      },
      productConfiguration: { featureFlags }
    } = stores

    const {
      uiStore: {
        dataStore: {
          summary: { clientOrder, doorOrders, client, ...rest }
        },
        isOrderEditable
      }
    } = useOrderContext()
    const { clusters, quantity: { doorProducts = {} } = {} } = rowData || {}
    // Get  Door order Id
    const { field: doorOrderId } = column
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const doorProduct = doorProducts?.[doorOrderId]

    const isRetail = useMemo(
      () => getEnumValue(VIPClientType.Internal) === clientType,
      [clientType]
    )

    const highlightDoors = useMemo(() => {
      if (!isRetail) return false
      const isBooked = Boolean(doorProduct)
      const { door } = find(doorOrders, doorOrder => doorOrder.id === doorOrderId)
      const isBookedOriginally =
        config.assortmentSource.retail === ASSORTMENT_SOURCE.CLUSTER_GROUP
          ? !isEmpty(intersectionBy(door?.clusterGroup?.clusters, clusters, 'id'))
          : some(clusters, ['id', door?.cluster.id])
      return (!isBooked && isBookedOriginally) || (isBooked && !isBookedOriginally)
    }, [clusters, doorOrderId, doorOrders, doorProduct, isRetail])

    const index = rowData?.id ? itemIndexObjectStore.getIndexByKey(rowData.id) : null
    const disabled = isNumber(index)
      ? itemIndexObjectStore.isObjectDataLoading(
          index,
          PRODUCT_OPERATIONS_LOADERS.UPDATING_DRAFT_QUANTITY
        ) ||
        itemIndexObjectStore.isObjectDataLoading(
          index,
          `${PRODUCT_OPERATIONS_LOADERS.BOOK_DOOR}_${doorOrderId}`
        ) ||
        itemIndexObjectStore.isObjectDataLoading(
          index,
          `${PRODUCT_OPERATIONS_LOADERS.UNBOOK_DOOR}_${doorOrderId}`
        ) ||
        // BOOKING_ALL_DOORS is intentionally left out, so we don't disable previously-booked doors
        itemIndexObjectStore.isObjectDataLoading(
          index,
          PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_QUANTITY
        ) ||
        itemIndexObjectStore.isObjectDataLoading(
          index,
          `${PRODUCT_OPERATIONS_LOADERS.UPDATING_DOOR_DRAFT_QUANTITY}_${doorOrderId}`
        ) ||
        itemIndexObjectStore.isObjectDataLoading(
          index,
          PRODUCT_OPERATIONS_LOADERS.SPLITTING_CLIENT_ORDER_SIZE_QUANTITY
        )
      : false
    const sizes =
      rowData &&
      (client?.captureSize || clientOrder?.client?.captureSize) &&
      showProductSizes[rowData?.id]
        ? getSizes(rowData.id)
        : []
    const isUnsized = rowData && isUnsizedProduct(rowData.id)

    const navigate = useCallback(
      (toNext: boolean) => {
        if (toNext) {
          navigateToNextEditableCell(index, visibleColIndex)
        } else {
          navigateToPrevAvailableCell(index, visibleColIndex)
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [index, visibleColIndex]
    )
    const focus = useCallback(
      () => {
        setRowAndColIndex(index, visibleColIndex)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [index, visibleColIndex]
    )
    const cellRef = useRef<QuantityCellRef>()
    useEffect(() => {
      if (!disabled && isActive) {
        if (!sizes.length) {
          setSizeIndex(-1)
        }
        if (sizeIndex === -1) {
          cellRef?.current?.focusQtyInput()
        } else {
          cellRef?.current?.focusSizeInput(sizeIndex)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cellRef, isActive, disabled, sizeIndex])
    const draftQtyChangeHandler = useCallback(
      e => {
        store.handleDoorQtyUpdate(
          rowData.id,
          doorOrderId,
          getNumberFromLocale(e.target.value)
        )
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [doorOrderId, rowData?.id]
    )
    const statusChangeHandler = useCallback(
      e => {
        bookUnbookDoors(
          clientOrder.clientOrderId || clientOrder.id,
          rowData.id,
          [doorOrderId],
          e.target.checked
        )
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [doorOrderId, rowData?.id, clientOrder]
    )

    const sizeQuantityChangeHandler = useCallback(
      (sizeId, e) => {
        store.handleDoorSizeQtyUpdate(
          rowData.id,
          doorOrderId,
          getNumberFromLocale(e.target.value),
          sizeId
        )
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [doorOrderId, doorProduct]
    )
    const isProductActive: boolean = useProductStatus(rowData)
    const isExclusionDoor: boolean =
      useExclusionDoor(rowData?.excludedInDoors, rest?.doorOrders, doorOrderId) &&
      !Boolean(doorProduct)

    if (!rowData) return <></>
    return (
      <QuantityCell
        disabled={!isProductActive || disabled || !isOrderEditable || isExclusionDoor}
        doorProduct={doorProduct}
        highlightDoors={
          featureFlags?.highlightBookUnbookDoorDiscrepanciesInSHR && highlightDoors
        }
        onIsBookedChanged={statusChangeHandler}
        navigateToCell={navigate}
        navigateToSize={sizeIndex => {
          focus()
          setSizeIndex(sizeIndex)
        }}
        onTotalByDraftQuantityChange={draftQtyChangeHandler}
        onTotalByDraftQuantityFocus={() => {
          focus()
          setSizeIndex(-1)
        }}
        onTotalByDraftQuantityKeyDown={() => {}}
        onTotalByDraftQuantityMouseDown={() => {}}
        showSizeQuantitySummary={client?.captureSize || clientOrder?.client?.captureSize}
        sizes={sizes}
        onSizeQuantityMouseDown={() => {}}
        onSizeQuantityChange={sizeQuantityChangeHandler}
        onSizeQuantityKeyDown={() => {}}
        onSizeQuantityFocus={sizeId => {
          focus()
          setSizeIndex(findIndex(sizes, ['id', sizeId]))
        }}
        onBlurHandler={() => {
          setRowAndColIndex(-1, -1)
        }}
        showBookCheckbox={true}
        isUnsized={isUnsized}
        ref={cellRef}
      />
    )
  })
